import type { NotificationList } from "@closer/shared";
import {
  NOTIFICATION_CRITICALITY_NAMES,
  NOTIFICATION_CRITICALITY_TONES,
  PERMISSION_DOMAIN_NAMES,
  TENANT_TYPE_NAMES,
} from "@closer/shared";
import { Badge, Skeleton } from "@closer/ui";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { Link } from "react-router";

import { BellIcon } from "../components/icons";
import { StateView } from "../components/StateView";
import { api } from "../lib/apiClient";
import { useAuth } from "../lib/authContext";
import { tenantKey } from "../lib/tenantQuery";
import { resolveErrorState } from "../lib/uiState";
import { TopBarPanel } from "./TopBarPanel";

/**
 * Üst Bar Bildirim paneli ve okunmamış rozeti (CLOS-103 `[064]`).
 *
 * ### ⚠ Bildirim ≠ Bekleyen İşlem
 *
 * Kartın ilk kriteri. Bekleyen İşlem (`[063]`) kullanıcıya ATANMIŞ bir iştir ve bir karar
 * bekler; bildirim yalnız haberdar eder. İkisi ayrı uçtan besleniyor, ayrı panelde
 * duruyor ve ayrı sayaç taşıyor. Tek kutuda birleştirmek, okumayı yapmakla karıştırırdı.
 *
 * ### ⚠ Öğeler TIKLANABİLİR DEĞİL — ve bu bilinçli
 *
 * Kartın kapsamı *"sınırlı bildirim önizlemesi ve tüm bildirimlere yönlendirme"* diyor:
 * yönlendirme Bildirim Merkezine, öğe başına DEĞİL. Kriter de açık — *"bildirimi okumak
 * ilgili işlemi tamamlamaz."* Panel hiçbir durum değiştirmiyor: okundu durumu, tercih ve
 * teslim CLOS-187'nin işi. Yapısal sonuç: buradan tek bir yazma isteği bile çıkmıyor.
 *
 * Bildirim Merkezine giden bağlantı ARTIK VAR (CLOS-186 `[168]`): merkez geldi ve panel
 * ona yol veriyor. Bağlantı bu kart yazılırken bilerek eklenmemişti — var olmayan bir
 * sayfaya götürürdü (ADR-0014, `[057]` ile aynı kural).
 *
 * ### Hata İZOLASYONU
 *
 * Kart: *"bir servis hatası diğer Üst Bar bileşenlerini durdurmaz."* Bildirim sorgusunun
 * hatası burada, panelin İÇİNDE karşılanıyor — dışarı bir istisna sızmıyor, dolayısıyla
 * çalışma alanı seçici, arama ve profil menüsü çalışmaya devam ediyor. Bir test bunu
 * kabuk seviyesinde sabitliyor (`AppLayout.test.tsx`).
 *
 * ### Eski veri ve yeniden doğrulama
 *
 * Yeniden doğrulama düşerse elimizdeki veri EKRANDA KALIYOR ama işaretleniyor. Hiç
 * göstermemek kullanıcıyı bilgisiz bırakırdı; güncelmiş gibi göstermek yalan olurdu.
 * Bağlantı döndüğünde tazeleme TanStack'in `refetchOnReconnect` varsayılanıyla
 * kendiliğinden koşuyor — elle bir dinleyici yazmak ikinci bir mekanizma doğururdu.
 */

/**
 * Önizlemede gösterilecek en fazla bildirim.
 *
 * Kart: *"SINIRLI bildirim önizlemesi."* Panel bir liste ekranı değil; sınırsız bir panel
 * üst barın altında ekranı kaplardı. Tam liste Bildirim Merkezinin (CLOS-186) işi.
 */
export const ONIZLEME_LIMITI = 5;

export function NotificationsPanel() {
  const { activeTenant, activeTenantId } = useAuth();
  const [open, setOpen] = useState(false);

  /**
   * Anahtar TENANT'A BAĞLI (`[043]` konvansiyonu): her alanın bildirimi kendi cache
   * girdisinde. Kartın *"çalışma alanı değiştiğinde eski alana ait rozet ve önizleme
   * temizlenir"* kriteri böylece yapısal — ayrı bir sıfırlama çağrısı yazılsaydı ikinci
   * bir gerçek kaynağı doğar ve biri unutulduğunda eski sayaç üst barda kalırdı.
   */
  const sorgu = useQuery({
    queryKey: activeTenantId
      ? tenantKey(activeTenantId, "notifications")
      : ["t", "yok", "notifications"],
    enabled: activeTenantId !== null,
    queryFn: async () =>
      (await api<NotificationList>("/api/notifications", { tenantId: activeTenantId! })).data,
    /**
     * ⚠ KONTROLLÜ POLLING (ADR-0037, CLOS-187 `[169]`).
     *
     * Aralık SUNUCUDAN geliyor (`config/notifications.json`); istemci bir sayı gömmüyor.
     * İlk yanıt gelene kadar poll yok — `false` dönmek "şimdilik yenileme" demek.
     *
     * ⚠ Arka planda DURUYOR: `refetchIntervalInBackground` varsayılanı `false` ve bu
     * varsayılan BİLEREK korunuyor. Görünmeyen bir sekmenin dakikalarca istek üretmesi,
     * tek sunucuda kendi kendine yük demekti; sekme öne geldiğinde
     * `refetchOnWindowFocus` zaten anında tazeliyor.
     */
    refetchInterval: (sorgu) => sorgu.state.data?.pollIntervalMs ?? false,
  });

  const bildirimler = sorgu.data?.items ?? [];
  /**
   * ⚠ Rozet SUNUCUDAN (CLOS-187). Listeden saymak, liste tavanı aşıldığında sessizce
   * yanlış sayı üretiyordu — kartın "rozet Bildirim Merkeziyle tutarlıdır" kriterinin
   * ihlali.
   */
  const okunmamis = sorgu.data?.unread ?? 0;

  /**
   * Hata sunumu ORTAK KAPIDAN (`[067]` CLOS-106): metin seçimi, teknik bilgi maskeleme ve
   * yeniden denemenin güvenliği orada karara bağlanıyor. Erişim kaybının METNİ domainin —
   * burada 403 "bu çalışma alanına erişimin değişti" demek (`overrides`).
   */
  const hataDurumu = sorgu.error
    ? resolveErrorState(sorgu.error, {
        idempotent: true,
        existenceIsSecret: false,
        overrides: {
          forbidden: {
            title: "Bu çalışma alanına erişiminiz değişti",
            description: "Bildirimleriniz görüntülenemiyor.",
          },
          error: {
            title: "Bildirimler yüklenemedi",
            description: "Bağlantı sorunları geçici olabilir.",
          },
        },
      })
    : null;
  /** Elde veri VAR ama son tazeleme düştü — kartın "eski veri" durumu. */
  const eskiVeri = sorgu.isError && sorgu.data !== undefined;

  const tetikleyici = (
    <button
      type="button"
      onClick={() => setOpen((onceki) => !onceki)}
      aria-haspopup="menu"
      aria-expanded={open}
      /** Sayı erişilebilir ADDA da var: rozet yalnız görsel olsaydı ekran okuyucu kaçırırdı. */
      aria-label={okunmamis > 0 ? `Bildirimler (${okunmamis} okunmamış)` : "Bildirimler"}
      /** Rozetin köşeye taşınma gerekçesi `PendingTasksPanel` ile aynı: sabit ikon ölçüsü. */
      className="relative flex size-11 shrink-0 items-center justify-center rounded-xl border border-border-default bg-surface text-fg-muted shadow-sm transition-colors hover:border-border-strong hover:text-fg-heading focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
    >
      <BellIcon />
      {/* Sıfır rozet DEĞİL: "0" basmak bakılacak bir şey varmış gibi görünür. */}
      {okunmamis > 0 ? (
        <span className="absolute -top-1.5 -right-1.5">
          <Badge tone="danger" className="px-1.5 py-0.5 text-[11px] leading-none shadow-sm">
            <span data-testid="bildirim-rozet">{okunmamis}</span>
          </Badge>
        </span>
      ) : null}
    </button>
  );

  return (
    <TopBarPanel
      open={open}
      onClose={() => setOpen(false)}
      title="Bildirimler"
      trigger={tetikleyici}
    >
      <div className="w-80 max-w-full">
        {activeTenant ? (
          <p className="border-b border-border-default px-3 py-2 text-xs text-fg-soft">
            {activeTenant.name} · {TENANT_TYPE_NAMES[activeTenant.type]}
          </p>
        ) : null}

        {/* Eski veri UYARISI listenin ÜSTÜNDE: kullanıcı içeriği okumadan önce ne kadar
            güvenebileceğini bilmeli. */}
        {eskiVeri ? (
          <p role="status" className="bg-warning/10 px-3 py-2 text-xs text-fg-strong">
            Bağlantı yenilenemedi, eski veri gösteriliyor
          </p>
        ) : null}

        {sorgu.isPending ? (
          <div role="status" aria-label="Bildirimler yükleniyor" className="p-3">
            <Skeleton lines={3} />
          </div>
        ) : hataDurumu && !eskiVeri ? (
          <div className="p-3">
            <StateView state={hataDurumu} onRetry={() => void sorgu.refetch()} />
          </div>
        ) : bildirimler.length === 0 ? (
          <div className="p-3">
            <StateView
              state={{
                kind: "error",
                title: "Bildiriminiz yok",
                description: "Bu çalışma alanındaki gelişmeler burada görünür.",
                retry: false,
              }}
            />
          </div>
        ) : (
          <ul className="py-1">
            {bildirimler.slice(0, ONIZLEME_LIMITI).map((b) => (
              <li
                key={b.id}
                data-testid="bildirim-ogesi"
                className="flex min-w-0 flex-col gap-0.5 px-3 py-2"
              >
                <span className="flex items-center gap-2">
                  {/* Okunmamış işareti METİNLE de taşınıyor: renk tek başına ayırt edici
                      olsaydı renk körü kullanıcı farkı göremezdi. */}
                  {b.readAt === null ? <Badge tone="danger">Yeni</Badge> : null}
                  {/*
                    ⚠ ÖNEM SEVİYESİ ARTIK GÖRÜNÜYOR (CLOS-1244 — kanon §12, §27.2).

                    Alan satırda yazılıyordu ama hiçbir yüzey okumuyordu: kullanıcı kritik
                    bir uyarıyı sıradan bir haberden ayırt edemiyordu.

                    ⚠ `INFO` ROZETSİZ ve bu bilinçli: her satıra "Bilgi" basmak, hiçbir şey
                    söylemeyen bir etiketi her satıra basmak olurdu. Ayırt edici olan,
                    sıradan olanın işaretsiz kalmasıdır.

                    ⚠ Seviye METİNLE anlatılıyor, yalnız renkle değil (rapor 4 §21).
                  */}
                  {NOTIFICATION_CRITICALITY_NAMES[b.criticality] ? (
                    <Badge
                      tone={NOTIFICATION_CRITICALITY_TONES[b.criticality]}
                      data-testid={`bildirim-seviye-${b.id}`}
                    >
                      {NOTIFICATION_CRITICALITY_NAMES[b.criticality]}
                    </Badge>
                  ) : null}
                  <span className="text-xs text-fg-soft">
                    {PERMISSION_DOMAIN_NAMES[b.category]}
                  </span>
                </span>
                <span className="text-sm text-fg">{b.title}</span>
              </li>
            ))}
          </ul>
        )}

        {/*
          ⚠ MERKEZE YOL HER DURUMDA VAR — boş listede de, hatada da.

          Yalnız dolu listede gösterseydik, önizlemede hiçbir şey görmeyen kullanıcı
          merkezin varlığını hiç öğrenemezdi. Kartın kapsamı da bunu söylüyor: panel
          "sınırlı önizleme ve TÜM BİLDİRİMLERE yönlendirme". Öğeler tıklanabilir değil;
          yönlendirme merkeze, öğe başına değil.
        */}
        <div className="border-t border-border-default px-3 py-2">
          <Link
            to="/app/bildirimler"
            onClick={() => setOpen(false)}
            className="text-sm font-medium text-primary hover:underline"
          >
            Tüm bildirimler
          </Link>
        </div>
      </div>
    </TopBarPanel>
  );
}
